/**
 * Der Einsatzablauf aus `07` §7.8 und ADR-045 — als Übergangstabelle.
 *
 * Das Diagramm dort ist Prosa und für Menschen. Diese Datei ist dieselbe
 * Sache für Maschinen, und sie existiert, weil `04` §4.12 eine Konfliktregel
 * darauf stützt: „Statusänderungen werden nur bei gültigem Zustandsübergang
 * akzeptiert." Ohne eine Tabelle, gegen die man prüfen kann, ist diese Regel
 * ein Satz und keine Prüfung.
 *
 * **Nicht hier: die Rechte.** Wer einen Übergang auslösen darf, steht in
 * ADR-045 und hängt an der Rolle des Handelnden. Die Trennung ist Absicht:
 * Ob ein Übergang fachlich möglich ist, entscheidet der Automat; ob ihn dieser
 * Benutzer machen darf, entscheidet die Autorisierung. Beides in einer Tabelle
 * zu führen hieße, jede Rechteänderung im Automaten nachzuziehen.
 */

export const EINSATZ_ZUSTAND = [
  "geplant",
  "anfahrt",
  "vor-ort",
  "wartet",
  "abschluss",
  "uebermittelt",
  "geprueft",
  "storniert",
] as const;

export type EinsatzZustand = (typeof EINSATZ_ZUSTAND)[number];

/**
 * Was von wo aus erreichbar ist.
 *
 * `storniert` steht bei jedem Zustand außer `geprueft` — so hat der Betreiber
 * es am 22.08.2026 entschieden (ADR-045). Die Folge ist bewusst in Kauf
 * genommen: Es gibt dann stornierte Einsätze mit bereits erfassten Zeiten und
 * Materialposten, und was bei der Übergabe an die Branchensoftware mit ihnen
 * geschieht, ist eine offene Frage an `EXP-001` (`09` §9.3).
 */
export const UEBERGAENGE: Readonly<Record<EinsatzZustand, readonly EinsatzZustand[]>> = {
  geplant: ["anfahrt", "storniert"],
  anfahrt: ["vor-ort", "storniert"],
  "vor-ort": ["wartet", "abschluss", "storniert"],
  wartet: ["vor-ort", "storniert"],
  abschluss: ["uebermittelt", "storniert"],
  // Zurück auf `abschluss`, wenn das Büro beanstandet. **Kein** Rücksprung auf
  // `vor-ort`: Ein Einsatz bleibt genau eine Anfahrt, sonst enthielte er
  // mehrere Vor-Ort-Zeiten mit Lücken dazwischen, und die Zuordnung von
  // Arbeitszeit zu Anfahrt wäre mehrdeutig. Bei abrechnungsfähigen Zeiten
  // (`PWA-004`) ist das kein akademisches Problem. Muss der Monteur wirklich
  // nochmal hin, ist das ein Folgeeinsatz — `07` §7.6 kennt dafür Relations.
  uebermittelt: ["geprueft", "abschluss", "storniert"],
  geprueft: [],
  storniert: [],
} as const;

/** Zustände, aus denen es keinen Weg mehr gibt. */
export const ENDZUSTAENDE: readonly EinsatzZustand[] = ["geprueft", "storniert"];

export function istZustand(wert: unknown): wert is EinsatzZustand {
  return typeof wert === "string"
    && (EINSATZ_ZUSTAND as readonly string[]).includes(wert);
}

/**
 * Ist dieser Übergang fachlich zulässig?
 *
 * Ein Übergang auf sich selbst ist **nicht** zulässig. Das ist keine Härte,
 * sondern die Antwort auf einen realen Fall: Eine doppelt zugestellte
 * Offline-Operation würde denselben Wechsel zweimal melden. Beim zweiten Mal
 * ist der Zustand schon erreicht, und ihn stillschweigend durchzuwinken hieße,
 * zwei Ereignisse zu protokollieren, wo eines stattfand. Die Dopplung gehört
 * über den Idempotenzschlüssel abgefangen, nicht hier.
 */
export function uebergangErlaubt(von: EinsatzZustand, nach: EinsatzZustand): boolean {
  return UEBERGAENGE[von].includes(nach);
}
